import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { $dashboardExpenseCategoriesStore, $initialCurrenciesAvailableStore } from '@lib/store';
import { useCurrencySymbol } from '@shared/hooks';
import { cn, ColorUtils, DateUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import { View } from '@widgets';
import { Icon, Select, Text } from '@shared/widgets';
import { CATEGORY_ICONS } from './icons';

interface DashboardExpenseCategoriesProps {
  className?: string;
}

export const DashboardExpenseCategories = (props: DashboardExpenseCategoriesProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const dashboardExpenseCategoriesStore = useUnit($dashboardExpenseCategoriesStore.store);

  const { t, i18n } = useTranslation('dashboard');
  const dates = useMemo(() => DateUtils.getLastMonths({ locale: i18n.language }), [i18n.language]);

  const [currency, setCurrency] = useState<string>(currenciesAvailableStore.data[0]);
  const [date, setDate] = useState<string>(dates[0].value);

  const symbol = useCurrencySymbol(currency);

  useEffect(() => {
    if (currency) $dashboardExpenseCategoriesStore.events.getExpenseCategories({ currency, date });
    return () => $dashboardExpenseCategoriesStore.events.clear();
  }, [currency, date]);

  const colors = useMemo(() => {
    if (PaletteUtils.isPersonalPalette) return PaletteUtils.expenseCategoriesPersonalColors;
    if (PaletteUtils.isVcfcPalette) return PaletteUtils.expenseCategoriesVcfcColors;
    return dashboardExpenseCategoriesStore.data.map(() => ColorUtils.getRandomColor());
  }, [dashboardExpenseCategoriesStore]);
  const options = useMemo<ApexOptions>(() => {
    return {
      chart: {
        id: 'expense',
        width: '100%',
        height: '100%',
        redrawOnParentResize: true,
        offsetX: 0,
        toolbar: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      plotOptions: {
        pie: {
          startAngle: 0,
          endAngle: 360,
          expandOnClick: true,
          offsetX: 0,
          offsetY: 0,
          customScale: 1,
          dataLabels: {
            offset: 0,
            minAngleToShowLabel: 50,
          },
          donut: {
            size: '80%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontFamily: '"Proxima Nova", sans-serif',
                fontWeight: 600,
                color: 'var(--text-slate)',
                offsetY: 40,
                formatter: function (val) {
                  const formatted = val.slice(0, 25);
                  if (val.length > 25) return formatted.concat('...');
                  return formatted;
                },
              },
              value: {
                show: true,
                fontSize: '34px',
                fontFamily: '"Prata", sans-serif',
                fontWeight: 400,
                color: 'var(--text-slate)',
                offsetY: 0,
                formatter: function (val) {
                  const format = FormatUtils.toMoneyFormat(parseFloat(val));
                  if (symbol) return symbol + format;
                  return format;
                },
              },
              total: {
                show: true,
                showAlways: false,
                label: t('card.expenseCategories.totalSpent'),
                fontSize: '13px',
                width: 10,
                fontFamily: '"Proxima Nova", sans-serif',
                fontWeight: 400,
                color: 'var(--text-slate)',
                formatter: function (w) {
                  return symbol + FormatUtils.toMoneyFormat(w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0));
                },
              },
            },
          },
        },
      },
      stroke: {
        // width: [4]
      },
      labels: dashboardExpenseCategoriesStore.data.map((ex) => {
        const category = ex.category.replace(/[^a-zA-Z]/gi, '').toLowerCase();
        return t(`card.expenseCategories.categories.${category}`);
      }),
      noData: {
        text: t('card.expenseCategories.loading'),
      },
      dataLabels: {
        enabled: false,
      },
      colors: colors,
      tooltip: {
        enabled: true,
        theme: 'dark',
        style: {
          fontSize: '14px',
          fontFamily: '"Proxima Nova", sans-serif',
        },
        x: {
          show: true,
          format: 'dd MMM',
          formatter: undefined,
        },
        y: {
          formatter: undefined,
          title: {
            formatter: (seriesName) => {
              if (symbol) return seriesName + ': ' + symbol;
              return seriesName;
            },
          },
        },
        marker: {
          show: true,
        },
      },
    };
  }, [dashboardExpenseCategoriesStore, symbol, colors, t]);
  const series = useMemo(() => dashboardExpenseCategoriesStore.data.map((ex) => ex.amount), [dashboardExpenseCategoriesStore, currency, date]);
  return (
    <div
      className={cn(
        'flex flex-col',
        PaletteUtils.isInsigniaPalette && 'gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
        props.className,
      )}
    >
      <div className={cn('flex justify-between', PaletteUtils.isVcfcPalette && ['items-center', 'max-md:flex-col max-md:items-start'])}>
        <Text tag='h2' weight={PaletteUtils.isVcfcPalette ? 'semibold' : 'medium'} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
          {t('card.expenseCategories.title')}
        </Text>
        <div className={cn('flex gap-4', PaletteUtils.isVcfcPalette && 'max-md:mt-3 max-md:w-full max-md:flex-col')}>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={(value) => setCurrency(value!)} />
          <Select
            wrapperClassName='min-w-40'
            items={dates.map((d) => ({ value: d.value, label: `${d.name} ${new Date(d.value).getFullYear()}` }))}
            value={date}
            onChange={(value) => setDate(value!)}
          />
        </div>
      </div>
      <View
        className={cn(PaletteUtils.isVcfcPalette && 'mt-4')}
        loading={dashboardExpenseCategoriesStore.loading}
        error={dashboardExpenseCategoriesStore.error}
        noData={!series.length || !options.labels?.length}
        onRefresh={() => $dashboardExpenseCategoriesStore.events.getExpenseCategories({ currency, date })}
      >
        <div className={cn('mt-16 flex items-center justify-around', PaletteUtils.isVcfcPalette && 'max-md:flex-col')}>
          <div className='flex items-center justify-around'>
            <div className='flex'>
              <Chart options={options} series={series} type='donut' height={400} width='100%' />
            </div>
          </div>
          <div
            className={cn(
              'flex flex-col justify-center',
              PaletteUtils.isVcfcPalette && 'max-md:mt-4 max-md:grid max-md:grid-cols-2 max-md:justify-items-center max-md:gap-2',
            )}
          >
            {dashboardExpenseCategoriesStore.data.map((ex, idx) => {
              const category = ex.category.replace(/[^a-zA-Z]/gi, '').toLowerCase();
              const icon = CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS];

              return (
                <div key={idx} className={cn('my-1 flex items-center', PaletteUtils.isVcfcPalette && 'max-md:w-1/2')}>
                  <div
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-sm border bg-slate-gray',
                      PaletteUtils.isVcfcPalette && 'max-md:flex-shrink-0',
                    )}
                    style={{ backgroundColor: colors[idx] }}
                  >
                    <Icon icon={icon} color='black' />
                  </div>
                  <Text className='ml-3 flex items-center text-center' weight='medium' color='secondary'>
                    {t(`card.expenseCategories.categories.${category}`)}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      </View>
    </div>
  );
};
